import { Link } from '../../../components/Link';
import { useAuth } from '../../../lib/auth';
import { breadcrumbSchema, Seo } from '../../../lib/seo';
import { useFeatures, type FeatureKind } from '../../../lib/useFeatures';
import { PublicFooter } from '../../../components/PublicFooter';
import { PublicHeader } from '../../../components/PublicHeader';
import { Button, cn } from '../../../components/ui';
import { Icon } from '../../../components/icons';

/**
 * Index landing for /features — one card per feature page. Mirrors the LandingPage
 * addon strip but lives at its own URL so /features can be a sensible sitemap
 * branch. Two groups:
 *   - Core features (no `kind` — always visible): Bot Detection, Email Verification,
 *     Domain Health. These are credit-metered core capabilities every workspace
 *     uses; can't be globally disabled.
 *   - Toggleable add-ons (have `kind`): when the super-admin disables one of these,
 *     its card disappears here AND the underlying /features/<slug> page redirects to /.
 */
interface FeatureCard {
  /** Toggle-flag kind for add-ons; undefined for always-on core features. */
  kind?: FeatureKind;
  slug: string;
  title: string;
  tagline: string;
  /**
   * One sentence of PROBLEM context under the tagline — what goes wrong without this layer.
   * The hub previously carried ~300 words of mostly nav chrome, which reads as thin to both
   * crawlers and a first-time visitor comparing vendors. This is the substance that makes the
   * page worth ranking; keep it concrete (a failure mode), not a restatement of the tagline.
   */
  context: string;
  accent: 'allow' | 'verify' | 'review' | 'accent';
  icon: React.ReactNode;
}

const FEATURES: ReadonlyArray<FeatureCard> = [
  // ── Core (always-on) ─────────────────────────────────────────────────
  {
    slug: 'bot-detection',
    title: 'Bot Anomaly Detection & Email Verification',
    tagline:
      'One coupled action per signup — cluster bot waves, score risk, and confirm the mailbox actually receives mail.',
    context:
      'Bot waves rarely reuse an address twice — they lean on plus-addressing, dotted Gmail variants and disposable domains. Clustering catches the shared pattern that per-address email verification misses on its own.',
    accent: 'accent',
    icon: <Icon.ShieldAlert width={28} height={28} />,
  },
  {
    kind: 'domain_health',
    slug: 'domain-health',
    title: 'Domain Health',
    tagline: 'SPF · DKIM · DMARC · MX — scored, cached, surfaced on every record.',
    context:
      "A sender's authentication posture predicts whether mail reaches the inbox at all. Every address you check returns the domain's SPF, DKIM, DMARC and MX state, cached so repeat lookups cost nothing.",
    accent: 'accent',
    icon: <Icon.ShieldCheck width={28} height={28} />,
  },
  // ── Toggleable add-ons ───────────────────────────────────────────────
  {
    kind: 'inbox_placement',
    slug: 'inbox-placement',
    title: 'Inbox Placement',
    tagline: 'See where your mail lands across Gmail / Yahoo / Outlook.',
    context:
      'Delivered is not the same as seen. Seed-inbox tests across the major providers show whether a campaign lands in Primary, Promotions or spam — before it goes out to a real list.',
    accent: 'verify',
    icon: <Icon.Mail width={28} height={28} />,
  },
  {
    kind: 'warmup_inbox',
    slug: 'warmup',
    title: 'Warmup',
    tagline: 'Auto-warm every sending mailbox you sell from — 1 mailbox or 50.',
    context:
      'A cold domain gets throttled or filtered for weeks. Warmup builds sending reputation on a schedule across a pooled network, per mailbox, so new senders ramp without daily babysitting.',
    accent: 'allow',
    icon: <Icon.Activity width={28} height={28} />,
  },
  {
    kind: 'phone_validation',
    slug: 'phone-validation',
    title: 'Phone Validation',
    tagline: 'Catch VoIP & toll-free before they pollute your SMS campaigns.',
    context:
      'VoIP and toll-free numbers pass a format check but fail on delivery. E.164 normalisation plus carrier lookup separates reachable mobile numbers from the rest before you pay to message them.',
    accent: 'allow',
    icon: <Icon.User width={28} height={28} />,
  },
  {
    kind: 'blacklist',
    slug: 'blacklist-checks',
    title: 'Blacklist Checks',
    tagline: '50+ industry blacklists on every domain — before deliverability bites.',
    context:
      "By the time open rates drop you have usually been listed for days. Checking 50+ RBLs on every domain and sending IP surfaces a listing while it's still cheap to get delisted.",
    accent: 'review',
    icon: <Icon.ShieldAlert width={28} height={28} />,
  },
];

const ACCENT_RING: Record<'allow' | 'verify' | 'review' | 'accent', string> = {
  allow: 'text-allow hover:border-allow/40',
  verify: 'text-verify hover:border-verify/40',
  review: 'text-review hover:border-review/40',
  accent: 'text-accent hover:border-accentdeep/50',
};

export function FeaturesIndexPage() {
  const { feature } = useFeatures();
  const user = useAuth((s) => s.user);
  // Core features (no kind) always visible; toggleable features respect the live flag.
  const visible = FEATURES.filter((f) => !f.kind || feature(f.kind));

  return (
    <>
      <Seo
        title="Features"
        description="Bot detection, email verification, domain health, inbox placement, warmup, phone validation and blacklist checks — every signal layer, one page each."
        path="/features"
        jsonLd={[
          breadcrumbSchema([
            { name: 'Home', url: 'https://sieveguard.com/' },
            { name: 'Features', url: 'https://sieveguard.com/features' },
          ]),
        ]}
      />
    <div data-portal className="mx-auto max-w-[1180px] px-6">
      <PublicHeader />

      <section className="mt-10 rounded-3xl border border-bordersoft bg-gradient-to-br from-accentdeep/10 to-panel/60 p-10">
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-panel px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
          <span className="h-1.5 w-1.5 rounded-full bg-accent" /> Features
        </span>
        <h1 className="mt-5 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight md:text-5xl">
          Every signal layer your email integrity stack needs.
        </h1>
        <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted">
          One workspace, one Stripe subscription, the full deliverability +
          identity-verification stack. Anomaly detection + mailbox verification +
          authentication checks at the core; inbox placement / warmup / phone /
          blacklist on the edges. Click into any feature for a full breakdown of
          what's included during your Trial, how it pairs with the others, and the
          live per-action cost.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to={user ? '/app' : '/register'}>
            <Button variant="primary" className="h-12 px-5">
              {user ? 'Open workspace' : 'Start free workspace'}{' '}
              <Icon.Play width={15} height={15} />
            </Button>
          </Link>
          <Link to="/pricing">
            <Button variant="ghost" className="h-12 px-5">
              See pricing
            </Button>
          </Link>
        </div>
      </section>

      <section className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {visible.map((f) => (
          <Link
            key={f.slug}
            to={`/features/${f.slug}`}
            className={cn(
              'group flex flex-col rounded-2xl border border-bordersoft bg-panel p-6 transition hover:-translate-y-0.5 hover:shadow-[0_20px_50px_rgba(0,0,0,0.18)]',
              ACCENT_RING[f.accent],
            )}
          >
            <div className={cn('inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-panel2', ACCENT_RING[f.accent])}>
              {f.icon}
            </div>
            <h2 className="mt-4 text-xl font-semibold text-txt">{f.title}</h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{f.tagline}</p>
            <p className="mt-2 flex-1 text-[12.5px] leading-relaxed text-faint">{f.context}</p>
            <div className={cn('mt-4 font-mono text-[12px]', ACCENT_RING[f.accent])}>
              Read more →
            </div>
          </Link>
        ))}
      </section>

      <PublicFooter />
    </div>
    </>
  );
}

// Default export required by React Router framework-mode route modules.
export default FeaturesIndexPage;
